import { createContext, FC, useContext, useMemo, useState, type ReactNode } from "react";
import { PERMISSIONS } from "../../constants/permissionKeys";
import { useHasPermission } from "../../hooks/useHasPermission";

export interface ShellActionsApi {
  /** 积分明细弹窗 */
  openPointsDetail: () => void;
  closePointsDetail: () => void;
  showPointsDetail: boolean;
  /** 积分充值弹窗 */
  openPurchase: () => void;
  closePurchase: () => void;
  showPurchase: boolean;
}

export interface ShellActionsProviderProps {
  children: ReactNode;
}

const ShellActionsContext = createContext<ShellActionsApi | null>(null);

/**
 * 侧栏「积分 / 购买」等入口的弹窗开关状态：
 * 状态只在此 Provider 内维护，SidebarTools 触发、ShellModals 消费，避免散落各页面。
 * 无积分权限时 open* 为 noop（第二层保险，防快捷键 / 埋点 / 其他入口误调用）。
 */
export const ShellActionsProvider: FC<ShellActionsProviderProps> = ({ children }) => {
  const hasPoints = useHasPermission(PERMISSIONS.POINTS_VIEW);
  const [showPointsDetail, setShowPointsDetail] = useState(false);
  const [showPurchase, setShowPurchase] = useState(false);

  const canOpen = hasPoints === true;

  const api = useMemo<ShellActionsApi>(
    () => ({
      openPointsDetail: () => {
        if (!canOpen) return;
        setShowPointsDetail(true);
      },
      closePointsDetail: () => setShowPointsDetail(false),
      showPointsDetail,
      openPurchase: () => {
        if (!canOpen) return;
        setShowPurchase(true);
      },
      closePurchase: () => setShowPurchase(false),
      showPurchase,
    }),
    [canOpen, showPointsDetail, showPurchase]
  );

  return (
    <ShellActionsContext.Provider value={api}>{children}</ShellActionsContext.Provider>
  );
};

export function useShellActions(): ShellActionsApi | null {
  return useContext(ShellActionsContext);
}
